import { Auth } from '@aws-amplify/auth'
import store from '@/store'
import { HttpError, sendHttpRequest } from './httpClient.js'

export const requestGemini = async ({
  messages,
  aiVersion,
  returnReader = false,
  streamText = false,
  signal
}) => {
  if (!messages || messages.length === 0) {
    throw new Error('Cannot make AI call with empty messages.')
  }

  const LAMBDA_URL = process.env.VUE_APP_CHAT_LAMBDA_URL
  let jwtToken
  try {
    const currentUser = await Auth.currentAuthenticatedUser()
    jwtToken = currentUser.signInUserSession.idToken.jwtToken
  } catch (authError) {
    throw new Error('User authentication failed. Cannot call AI service.')
  }

  const mappedMessages = messages.map((message) => message.content)

  const requestBody = {
    aiVersion,
    messages: [mappedMessages.join('\n\n')],
    streamText
  }

  const requestHeaders = {
    Authorization: `Bearer ${jwtToken}`,
    'Content-Type': 'text/plain;charset=UTF-8'
  }

  try {
    const response = await sendHttpRequest(
      LAMBDA_URL,
      'POST',
      requestHeaders,
      JSON.stringify(requestBody),
      signal
    )

    let responseText
    if (!streamText && !returnReader) {
      responseText = await response.text()
    }

    if (!streamText && !returnReader && !responseText?.includes('jsonobjectdelimiter')) {
      const response = JSON.parse(responseText)
      if (response?.error?.message) {
        throw new Error(response.error.message)
      }
    }

    if (returnReader) {
      if (!response.body) {
        throw new Error('Response body is null, cannot return reader.')
      }
      return response.body.getReader()
    }

    const rawText = responseText.replace('jsonobjectdelimiter', '')

    if (rawText === null || typeof rawText === 'undefined') {
      return ''
    }

    let parsableJsonString = rawText ? rawText.trim() : ''

    if (parsableJsonString) {
      const jsonRegex = /^```(?:json)?\s*([\s\S]*?)\s*```$/
      const match = parsableJsonString.match(jsonRegex)

      if (match && match[1]) {
        parsableJsonString = match[1].trim()
      } else {
        if (parsableJsonString.startsWith('```') && parsableJsonString.endsWith('```')) {
          const simplerStrip = parsableJsonString.substring(3, parsableJsonString.length - 3).trim()
          if (simplerStrip.startsWith('{') || simplerStrip.startsWith('[')) {
            parsableJsonString = simplerStrip
          }
        }
      }
    }

    return JSON.parse(parsableJsonString)
  } catch (error) {
    if (error.name === 'AbortError') {
      throw error
    }

    if (error instanceof HttpError) {
      throw new Error(`AI Lambda service call failed: ${error.message}. Server detail: ${error.body || 'N/A'}`)
    }

    store.commit('snackbar/showMessage', {
      content: error.message || 'AI failed to respond to the query, please try again later',
      timeout: 6000,
      color: 'red'
    }, { root: true })

    throw new Error(`AI Lambda service call failed: ${error.message}`)
  }
}
